import React, { useState, useEffect } from "react"; // eslint-disable-line no-unused-vars
import {
  Grid2 as Grid,
  Rating,
  Typography,
  Link,
  Paper,
  IconButton,
} from "@mui/material";
import { Charity, CodeDetails } from "../../../shared/types";
import * as consts from "../../../shared/utilities/consts";
import { getCNData } from "../../../shared/utilities/actions";
import { Help as HelpIcon } from "@mui/icons-material";

export interface CNRatingProps {
  charity: Charity;
  codeDetails: CodeDetails;
}

export const CNRating = ({
  charity,
  codeDetails,
}: CNRatingProps): React.JSX.Element => {
  const [rating, setRating] = useState<string>("");
  const [beaconScore, setBeaconScore] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      let cnData = await getCNData(charity.ein);
      setRating(
        cnData?.data.publicSearchFaceted.results[0].encompass_star_rating
      );
      setBeaconScore(
        Math.round(
          Number(cnData?.data.publicSearchFaceted.results[0].encompass_score)
        )
      );
    };

    fetchData();
  }, []);

  const renderStars = () => {
    if (rating.toLocaleLowerCase() == "not rated") {
      return null;
    }
    if (beaconScore >= 90) {
      return (
        <img
          style={{ margin: "auto", maxWidth: "225px" }}
          src={`${consts.BLOB_URL}/v4-web-resources/cn_four_star.svg`}
          alt="Great"
        />
      );
    } else if (beaconScore < 90 && beaconScore >= 75) {
      return (
        <img
          style={{ margin: "auto", maxWidth: "225px" }}
          src={`${consts.BLOB_URL}/v4-web-resources/cn_three_star.svg`}
          alt="Good"
        />
      );
    } else if (beaconScore < 75 && beaconScore >= 60) {
      return (
        <img
          style={{ margin: "auto", maxWidth: "225px" }}
          src={`${consts.BLOB_URL}/v4-web-resources/cn_two_star.svg`}
          alt="Needs Improvement"
        />
      );
    } else if (beaconScore < 60 && beaconScore >= 50) {
      return (
        <img
          style={{ margin: "auto", maxWidth: "225px" }}
          src={`${consts.BLOB_URL}/v4-web-resources/cn_one_star.svg`}
          alt="Poor"
        />
      );
    } else if (beaconScore > 0 && beaconScore < 50) {
      return (
        <img
          style={{ margin: "auto", maxWidth: "225px" }}
          src={`${consts.BLOB_URL}/v4-web-resources/cn_zero_star.svg`}
          alt="Very Poor"
        />
      );
    } else {
      return null;
    }
  };

  const renderText = () => {
    if (rating.toLocaleLowerCase() == "not rated") {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Not Currently Rated{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    }
    if (beaconScore >= 90) {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Four-Star Rating
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    } else if (beaconScore < 90 && beaconScore >= 75) {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Three-Star Rating{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    } else if (beaconScore < 75 && beaconScore >= 60) {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Two-Star Rating{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    } else if (beaconScore < 60 && beaconScore >= 50) {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          One-Star Rating{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    } else if (beaconScore > 0 && beaconScore < 50) {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Zero-Star Rating{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    } else {
      return (
        <Typography
          variant="body1"
          component="div"
          sx={{ textAlign: "center" }}
        >
          Not Currently Rated{" "}
          <IconButton
            onClick={() => {
              window.open(
                "https://www.charitynavigator.org/about-us/our-methodology/ratings/"
              );
            }}
          >
            <HelpIcon />
          </IconButton>
        </Typography>
      );
    }
  };

  return (
    <Paper
      sx={{
        padding: "12px",
        border: "1px solid #cdd1e0",
        borderRadius: "12px",
      }}
    >
      <Grid size={{ xs: 12 }} container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h4" sx={{ textAlign: "center" }}>
            Charity Navigator Rating{" "}
            {rating.toLocaleLowerCase() != "not rated" && beaconScore > 0
              ? `- ${beaconScore}%`
              : `- N/A`}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ marginTop: "6px", marginBottom: "6px" }}>
          {renderStars()}
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ marginTop: "6px", marginBottom: "6px" }}>
          {renderText()}
        </Grid>
        {!codeDetails.code && (
          <Grid
            size={{ xs: 12 }}
            sx={{ marginBottom: "6px", textAlign: "center" }}
          >
            <Typography variant="body1" component="div">
              No TisBest Card? Donate via{" "}
              <Link
                href={`https://www.charitynavigator.org/ein/${charity.ein}?donateNow=true&c_src=TISBEST&utm_source=tisbest&utm_medium=referral`}
                target="_blank"
              >
                Charity Navigator
              </Link>
              .
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};
